import { attachDictionaryRows } from "@/features/dictionaries/attachDictionaryRows";
import { supabaseClient } from "@/lib/supabase";
import type { InputT } from "@patient-watch/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";

// Function to select multiple inputs by an array of UUIDs
export async function selectInputsByIds(inputIds: string[]): Promise<InputT[]> {
    const ids = (inputIds ?? []).filter((id) => validateUUID(id));
    if (ids.length === 0) return [];

    // A dictionary-bound input needs its choices to render and to save a code, so bring them along.
    const { data, error } = await supabaseClient
        .from("inputs")
        .select("*, dictionary:dictionaries(*, options(*))")
        .in("input_id", ids);

    if (error) throw error;
    return orderInputsByRequestedIds(ids, ((data ?? []) as unknown as InputT[]).map(attachDictionaryRows));
}

export function orderInputsByRequestedIds(ids: string[], rows: InputT[]): InputT[] {
    const byId = new Map(rows.map((row) => [row.input_id, row]));
    return ids.flatMap((id) => {
        const row = byId.get(id);
        return row ? [row] : [];
    });
}

export const selectInputsByIdsQueryOptions = (inputIds: string[]) => {
    // Explicitly check if inputIds is a valid array before using it
    const isEnabled = Array.isArray(inputIds) && inputIds.length > 0;
    // Ensure the queryKey part is always an array, even if inputIds is somehow invalid
    const safeQueryKeyIds = Array.isArray(inputIds) ? inputIds : [];

    return queryOptions({
        queryKey: ["inputs", { input_ids: safeQueryKeyIds }],
        // selectInputsByIds already handles invalid inputIds internally
        queryFn: async () => selectInputsByIds(inputIds),
        enabled: isEnabled,
    });
};

export function useSelectInputsByIds(inputIds: string[]) {
    // Ensure inputIds is always an array before passing to options
    const safeInputIds = Array.isArray(inputIds) ? inputIds.filter((id) => typeof id === "string") : [];
    const options = selectInputsByIdsQueryOptions(safeInputIds);
    return useQuery(options);
}
